import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { TouchpointPerson } from "@/hooks/api/use-touchpoint-persons";
import { api, generateQueryKey } from "@/lib/api";

/**
 * Hook to fetch touchpoint persons data aggregated across all accounts associated with a person
 * Provides lookup functions to get person names and emails
 * @param personId - The person ID to fetch touchpoint persons for
 * @returns Person lookup functions
 */
export function usePersonTouchpointPersons(personId?: string) {
  // Fetch aggregated touchpoint persons from server
  const {
    data: persons,
    isLoading,
    error,
  } = useQuery<TouchpointPerson[]>({
    queryKey: generateQueryKey(
      `datahub/persons/${encodeURIComponent(
        personId || "",
      )}/touchpoint-persons/`,
    ),
    queryFn: () =>
      api.get<TouchpointPerson[]>(
        `datahub/persons/${encodeURIComponent(
          personId || "",
        )}/touchpoint-persons/`,
      ),
    enabled: !!personId,
  });

  // Create memoized lookup maps
  const { personByIdMap, personByEmailMap } = useMemo(() => {
    const byId = new Map<string, TouchpointPerson>();
    const byEmail = new Map<string, TouchpointPerson>();

    if (persons) {
      persons.forEach((person) => {
        byId.set(person.person_id, person);
        if (person.email_address) {
          const normalized = person.email_address.toLowerCase().trim();
          if (!byEmail.has(normalized)) {
            byEmail.set(normalized, person);
          }
        }
      });
    }

    return { personByIdMap: byId, personByEmailMap: byEmail };
  }, [persons]);

  // Helper to format person's name consistently
  const formatPersonName = useCallback((person: TouchpointPerson) => {
    const name = [person.first_name, person.last_name]
      .filter(Boolean)
      .join(" ");
    return name || person.email_address || person.person_id;
  }, []);

  const getPersonById = useCallback(
    (personId: string) => personByIdMap.get(personId),
    [personByIdMap],
  );

  const getPersonName = useCallback(
    (personId: string) => {
      if (!personId) return "Unknown";

      const person = personByIdMap.get(personId);
      if (!person) {
        return personId;
      }
      return formatPersonName(person);
    },
    [personByIdMap, formatPersonName],
  );

  const getPersonEmail = useCallback(
    (personId: string) => {
      const person = personByIdMap.get(personId);
      return person?.email_address || "";
    },
    [personByIdMap],
  );

  // Email -> Display Name lookup (fallback to the email if unknown)
  const getNameByEmail = useCallback(
    (emailAddress?: string | null) => {
      if (!emailAddress) return "";
      const normalized = emailAddress.toLowerCase().trim();
      const person = personByEmailMap.get(normalized);
      if (!person) return emailAddress;
      return formatPersonName(person);
    },
    [personByEmailMap, formatPersonName],
  );

  return {
    persons: Array.from(personByIdMap.values()),
    getPersonById,
    getPersonName,
    getPersonEmail,
    getNameByEmail,
    isLoading,
    error,
  };
}
